import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { BoardStateService } from './board.state.service';
import { Configuration } from '../../../../models/configurators-2d/applications/fence/configuration';
import { distinct } from '../../../../services/helpers/utils';
import { Color } from '../../../../models/configurators-2d/applications/fence/color';

@Injectable({
  providedIn: 'root',
})
export class ColorStateService {
  /**
   * Liste des lames
   */
  colorsByInstallations: WritableSignal<Map<number, Color[]>> = signal(new Map<number, Color[]>());

  /**
   * Indique si le service est entrain de faire des changements
   */
  loading: WritableSignal<boolean> = signal(false);

  /**
   * Nombre de couleurs actives
   * premier number: installationId
   * second number: nombre de couleurs actives
   */
  colorsActiveCountByInstallations: Signal<Map<number, number>> = computed(() => {
    const colorsActiveCountByInstallation = new Map<number, number>();

    this.colorsByInstallations().forEach((colors, installationId) => {
      colorsActiveCountByInstallation.set(installationId, colors.filter(c => !c.isDisabled).length);
    });

    return colorsActiveCountByInstallation;
  });

  private readonly boardStateService = inject(BoardStateService);

  /**
   * Récupère la liste des lames à partir du type de lame sélectionné
   * @param configuration
   */
  setList(configuration: Configuration): void {
    const boardByInstallations = this.boardStateService.boardByInstallations;

    this.loading.set(true);

    const colorsByInstallations = new Map<number, Color[]>();

    for (const configurationInstallation of configuration.configurationInstallations) {
      const boards = boardByInstallations().get(configurationInstallation.installationId);

      // id des types de lames utilisés dans la pose
      const boardIds = distinct(configurationInstallation.configurationBoards.flatMap(b => b.fenceTypeId)) as number[];

      // types de lames utilisés dans la pose
      const configurationBoards = boards.filter(b => boardIds.includes(b.id));

      // lames liées aux types de lames utilisés dans la pose
      const colors: Color[] = configurationBoards.flatMap(b => b.colors).filter(c => !c.isDisabled);

      // lame déjà sélectionnée dans la pose
      const configurationColorIds = configuration.configurationInstallations
        .map(i => i.configurationBoards.map(b => b.boardId))
        .flat();

      const configurationColors = boards.flatMap(b => b.colors).filter(c => configurationColorIds.includes(c.id));

      if (configurationColors.some(c => c.isDisabled)) {
        colors.push(...configurationColors.filter(c => c.isDisabled));
      }

      colorsByInstallations.set(
        configurationInstallation.installationId,
        distinct(colors, c => c.id)
      );
    }

    this.colorsByInstallations.set(colorsByInstallations);

    this.loading.set(false);
  }

  /**
   * Récupère une lame par couleur
   * @param installationId
   * @param id
   */
  getByInstallationIdAndId(installationId: number, id: number): Color | undefined {
    return this.colorsByInstallations()
      .get(installationId)
      .find(c => c.id === id);
  }

  /**
   * Récupère une lame par couleur
   * @param id
   */
  getById(id: number): Color | undefined {
    for (const [_, colors] of this.colorsByInstallations()) {
      const color = colors.find(c => c.id === id);

      if (color) {
        return color;
      }
    }
  }

  /**
   * Récupère une liste de lames par liste d'identifiants
   * @param installationId
   * @param ids
   */
  getByInstallationIdAndIds(installationId: number, ids: number[]) {
    return this.colorsByInstallations()
      .get(installationId)
      .filter(c => ids.includes(c.id));
  }
}
